import React, {useEffect, useState} from "react";
import PlotlyPlot from "../../../../common/components/PlotlyPlot";
import {loadProtocolRMSSNRData} from "../../services/protocols";
import {PlotParams} from "react-plotly.js";
import {IProtocol} from "../../../types/protocol";
import {ICompany} from "../../../../common/types";
import {Box, Loader} from "@mantine/core";

interface RmsSnrChartProps {
    activeCompany: ICompany;
    selectedProtocol: IProtocol | undefined;
    refreshTrigger?: number
}

export const RmsSnrChart: React.FC<RmsSnrChartProps> = ({activeCompany, selectedProtocol, refreshTrigger}) => {
    const [rmsSnrData, setRmsSnrData] = useState<PlotParams>()
    const [loading, setLoading] = useState<boolean>(false)

    useEffect(() => {
        if (selectedProtocol) {
            setLoading(true)
            loadProtocolRMSSNRData(activeCompany.id, selectedProtocol).then((data) => {
                // transform streamed rows [time_iso, channel_name, rms, snr]
                const channelSet = new Set<string>();
                const grouped = new Map<string, Map<string, { rms: number | null; snr: number | null }>>();

                // collect channels and group by timestamp (preserve first-seen order)
                data.data.forEach(([time, channel, rms, snr]: [string, string, number | null, number | null]) => {
                    channelSet.add(channel);
                    if (!grouped.has(time)) {
                        grouped.set(time, new Map());
                    }
                    grouped.get(time)!.set(channel, {
                        rms: rms === undefined ? null : rms,
                        snr: snr === undefined ? null : snr
                    });
                });
                const channelOrder = Array.from(channelSet).sort();
                // build traces: two traces per timestamp (RMS -> y, SNR -> y2)
                const traces: any[] = [];
                const colorPalette = [
                    "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
                    "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf"
                ];
                let timeIndex = 0;
                grouped.forEach((chanMap, time) => {
                    const color = colorPalette[timeIndex % colorPalette.length];
                    timeIndex += 1;

                    const rmsTraceBase = (data.figure.data[0] || {}) as any;
                    const snrTraceBase = (data.figure.data[1] || {}) as any;

                    const rmsY = channelOrder.map((ch) => {
                        const v = chanMap.get(ch);
                        return v ? v.rms : null;
                    });
                    const snrY = channelOrder.map((ch) => {
                        const v = chanMap.get(ch);
                        return v ? v.snr : null;
                    });

                    traces.push({
                        ...rmsTraceBase,
                        name: `${time}`,
                        x: channelOrder,
                        y: rmsY,
                        // ensure both marker and line use the same color
                        marker: {...(rmsTraceBase.marker || {}), color},
                        line: {...(rmsTraceBase.line || {}), color},
                    });

                    traces.push({
                        ...snrTraceBase,
                        name: `${time}`,
                        x: channelOrder,
                        y: snrY,
                        marker: {...(snrTraceBase.marker || {}), color},
                        line: {...(snrTraceBase.line || {}), color},
                    });
                });
                // convert to plotly params format
                // set PlotParams state using server-provided layout
                setRmsSnrData({
                    layout: {
                        ...data.figure.layout,
                    },
                    config: {responsive: true},
                    data: traces,
                });
            }).catch(() => {
                // eat the error since notification would already be called
            }).finally(() => {
                setLoading(false)
            })
        }
    }, [selectedProtocol, refreshTrigger])

    const renderLoading = () => {
        return (
            <Box style={{position: "absolute", zIndex: "10", top: "calc(50% - 2em)", left: "calc(50% - 2em)"}}>
                <Loader size="4em" color="var(--bios-strong-accent)"/>
            </Box>
        )
    }

    if (rmsSnrData) {
        return (
            <Box>
                <PlotlyPlot {...rmsSnrData}/>
                {
                    loading ? renderLoading() : <></>
                }
            </Box>
        )
    } else {
        renderLoading()
    }
}
